import React from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, Eye, Keyboard, Users, Globe, Heart } from 'lucide-react';

const AccessibilityPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isZh = i18n.language === 'zh';

  const accessibilityFeatures = [
    {
      icon: <Eye className="h-8 w-8 text-blue-600" />,
      title: isZh ? '视觉无障碍' : 'Visual Accessibility',
      description: isZh 
        ? '高对比度设计、可调字体大小、屏幕阅读器优化' 
        : 'High contrast design, adjustable font sizes, screen reader optimization',
      features: isZh 
        ? ['WCAG 2.1 AA标准', '色彩对比度4.5:1', '语义化HTML标记']
        : ['WCAG 2.1 AA compliant', '4.5:1 color contrast ratio', 'Semantic HTML markup']
    },
    {
      icon: <Keyboard className="h-8 w-8 text-green-600" />,
      title: isZh ? '键盘导航' : 'Keyboard Navigation',
      description: isZh 
        ? '完整的键盘操作支持，无需鼠标即可访问所有功能' 
        : 'Full keyboard operation support, access all features without a mouse',
      features: isZh 
        ? ['Tab键导航', '焦点指示器', '快捷键支持']
        : ['Tab navigation', 'Focus indicators', 'Keyboard shortcuts']
    },
    {
      icon: <Users className="h-8 w-8 text-purple-600" />,
      title: isZh ? '认知无障碍' : 'Cognitive Accessibility',
      description: isZh 
        ? '清晰的界面布局、简化的导航流程、错误提示优化' 
        : 'Clear interface layout, simplified navigation flow, optimized error messages',
      features: isZh 
        ? ['直观的用户界面', '清晰的指导说明', '错误恢复机制']
        : ['Intuitive user interface', 'Clear instructions', 'Error recovery mechanisms']
    },
    {
      icon: <Globe className="h-8 w-8 text-orange-600" />,
      title: isZh ? '多语言支持' : 'Multilingual Support',
      description: isZh 
        ? '支持中文和英文，确保不同语言用户的访问体验' 
        : 'Support for Chinese and English, ensuring access for users of different languages',
      features: isZh 
        ? ['双语界面', '文化适配', '本地化内容']
        : ['Bilingual interface', 'Cultural adaptation', 'Localized content']
    }
  ];

  const seoFeatures = [
    {
      title: isZh ? '搜索引擎优化优势' : 'SEO Benefits',
      items: isZh 
        ? ['提升12%有机搜索流量', '改善搜索引擎排名', '增强网站可信度', '扩大用户覆盖面']
        : ['12% increase in organic search traffic', 'Improved search engine rankings', 'Enhanced website credibility', 'Expanded user reach']
    },
    {
      title: isZh ? '技术实现' : 'Technical Implementation',
      items: isZh 
        ? ['语义化HTML5标记', '完整的ARIA标签', '结构化数据', '性能优化']
        : ['Semantic HTML5 markup', 'Complete ARIA labeling', 'Structured data', 'Performance optimization']
    }
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <Helmet>
        <title>{isZh ? '无障碍访问 - Resonow' : 'Accessibility - Resonow'}</title>
        <meta 
          name="description" 
          content={isZh 
            ? 'Resonow致力于为所有用户提供无障碍的疗愈服务平台，符合WCAG 2.1 AA标准，支持键盘导航、屏幕阅读器等辅助技术。'
            : 'Resonow is committed to providing an accessible healing platform for all users, compliant with WCAG 2.1 AA standards, supporting keyboard navigation, screen readers and assistive technologies.'
          } 
        />
        <meta name="keywords" content={isZh ? '无障碍, 可访问性, WCAG, 包容性设计, 疗愈平台' : 'accessibility, WCAG, inclusive design, healing platform, assistive technology'} />
        <link rel="canonical" href={`https://resonow.org/accessibility`} />
      </Helmet>

      <main className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-50 pt-20">
        <div className="container mx-auto px-4 py-12">
          {/* Hero Section */}
          <section className="text-center mb-16">
            <div className="flex justify-center mb-6">
              <Heart className="h-16 w-16 text-amber-600" />
            </div>
            <h1 className="text-4xl md:text-5xl font-serif text-earth-900 mb-6" role="heading" aria-level={1}>
              {isZh ? '人人可及的疗愈平台' : 'Healing for Everyone'}
            </h1>
            <p className="text-xl text-earth-700 max-w-3xl mx-auto mb-8">
              {isZh 
                ? 'Resonow致力于创建一个包容性的疗愈平台，确保每个人都能平等地获得精神健康和疗愈服务，无论其能力或需求如何。'
                : 'Resonow is committed to creating an inclusive healing platform that ensures everyone has equal access to mental health and healing services, regardless of their abilities or needs.'
              }
            </p>
            <Badge variant="secondary" className="bg-green-100 text-green-800 text-lg px-6 py-2">
              <CheckCircle className="h-5 w-5 mr-2" />
              {isZh ? 'WCAG 2.1 AA 认证' : 'WCAG 2.1 AA Certified'}
            </Badge>
          </section>

          {/* Accessibility Features */}
          <section className="mb-16">
            <h2 className="text-3xl font-serif text-earth-900 text-center mb-12" role="heading" aria-level={2}>
              {isZh ? '无障碍功能特色' : 'Accessibility Features'}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {accessibilityFeatures.map((feature, index) => (
                <Card key={index} className="border-2 hover:border-amber-300 transition-colors">
                  <CardHeader>
                    <div className="flex items-center space-x-4">
                      {feature.icon}
                      <CardTitle className="text-xl" role="heading" aria-level={3}>
                        {feature.title}
                      </CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-earth-700 mb-4">{feature.description}</p>
                    <ul className="space-y-2" role="list">
                      {feature.features.map((item, idx) => (
                        <li key={idx} className="flex items-center text-sm text-earth-600">
                          <CheckCircle className="h-4 w-4 text-green-600 mr-2 flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          {/* SEO and Technical Benefits */}
          <section className="mb-16">
            <h2 className="text-3xl font-serif text-earth-900 text-center mb-12" role="heading" aria-level={2}>
              {isZh ? '技术优势与SEO效益' : 'Technical Benefits & SEO Impact'}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {seoFeatures.map((section, index) => (
                <Card key={index} className="bg-white/80 backdrop-blur-sm">
                  <CardHeader>
                    <CardTitle className="text-xl text-amber-700" role="heading" aria-level={3}>
                      {section.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3" role="list">
                      {section.items.map((item, idx) => (
                        <li key={idx} className="flex items-start">
                          <CheckCircle className="h-5 w-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                          <span className="text-earth-700">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          {/* Commitment Statement */}
          <section className="text-center bg-white/90 backdrop-blur-sm rounded-2xl p-8 border border-amber-200">
            <h2 className="text-2xl font-serif text-earth-900 mb-4" role="heading" aria-level={2}>
              {isZh ? '我们的承诺' : 'Our Commitment'}
            </h2>
            <p className="text-earth-700 max-w-2xl mx-auto">
              {isZh 
                ? '我们持续投资于无障碍技术，定期审核和改进我们的平台，确保为所有用户提供最佳的体验。我们相信，真正的疗愈应该对每个人都可及。'
                : 'We continuously invest in accessibility technology, regularly audit and improve our platform to ensure the best experience for all users. We believe that true healing should be accessible to everyone.'
              }
            </p>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default AccessibilityPage;